import {
   Box,
   BoxProps,
   Button,
   ButtonProps,
   Flex,
   FlexProps,
   HStack,
   IconButton,
   IconButtonProps,
   StackProps,
} from '@chakra-ui/react';
import { useDisclosure } from '@ifixit/ui/hooks/use-disclosure';
import { forwardRef, useRef, useMemo } from 'react';
import type { AnchorHTMLAttributes } from 'react';
import { Context, HeaderContext, useHeaderContext } from './context';

export const Header = forwardRef<HTMLElement, FlexProps>((props, ref) => {
   const hiddenBar = useDisclosure();
   const navigation = useDisclosure();
   const navigationToggleButtonRef = useRef<HTMLButtonElement>(null);

   const context = useMemo(
      (): Context => ({
         hiddenBar: {
            isOpen: hiddenBar.isOpen,
            open: hiddenBar.onOpen,
            close: hiddenBar.onClose,
         },
         navigation: {
            isOpen: navigation.isOpen,
            toggleButtonRef: navigationToggleButtonRef,
            toggle: navigation.onToggle,
            close: navigation.onClose,
         },
      }),
      [
         hiddenBar.isOpen,
         hiddenBar.onOpen,
         hiddenBar.onClose,
         navigation.isOpen,
         navigation.onToggle,
         navigation.onClose,
      ]
   );

   return (
      <HeaderContext.Provider value={context}>
         <Flex
            ref={ref}
            as="header"
            className="nextjs-header"
            bg="black"
            color="white"
            height="header"
            overflow={{
               base: 'hidden',
               md: 'visible',
            }}
            direction="column"
            pl={{
               base: '0',
               lg: '3',
            }}
            pr={{
               base: '3',
               lg: '3',
            }}
            {...props}
         />
      </HeaderContext.Provider>
   );
});
Header.displayName = 'Header';

export const HeaderBar = forwardRef<HTMLDivElement, FlexProps>((props, ref) => {
   const context = useHeaderContext();
   return (
      <Flex
         ref={ref}
         alignItems="center"
         h="full"
         flexShrink={0}
         w="full"
         maxW="1400px"
         mx="auto"
         transition="all 300ms"
         opacity={context.hiddenBar.isOpen ? 0 : 1}
         justify="space-between"
         transform={{
            base: context.hiddenBar.isOpen ? 'translateY(0)' : 'translateY(-100%)',
            md: 'initial',
         }}
         {...props}
      />
   );
});
HeaderBar.displayName = 'HeaderBar';

export const HeaderPrimaryNavigation = forwardRef<HTMLDivElement, FlexProps>((props, ref) => (
   <Flex ref={ref} align="center" h="full" {...props} />
));
HeaderPrimaryNavigation.displayName = 'HeaderPrimaryNavigation';

export const HeaderSecondaryNavigation = forwardRef<HTMLDivElement, StackProps>((props, ref) => (
   <HStack ref={ref} align="center" spacing={2} {...props} />
));
HeaderSecondaryNavigation.displayName = 'HeaderSecondaryNavigation';

export const HeaderHiddenBar = forwardRef<HTMLDivElement, FlexProps>((props, ref) => {
   const context = useHeaderContext();
   return (
      <Flex
         ref={ref}
         h="full"
         flexShrink={0}
         transition="all 300ms"
         transform={context.hiddenBar.isOpen ? 'translateY(0)' : 'translateY(-100%)'}
         opacity={context.hiddenBar.isOpen ? 1 : 0}
         display={{
            base: 'flex',
            md: 'none',
         }}
         align="center"
         pl="6"
         {...props}
      />
   );
});
HeaderHiddenBar.displayName = 'HeaderHiddenBar';

export const WordmarkLink = forwardRef<
   HTMLAnchorElement,
   BoxProps & AnchorHTMLAttributes<HTMLAnchorElement>
>((props, ref) => (
   <Box
      ref={ref}
      as="a"
      display="inline-block"
      cursor="pointer"
      borderRadius="md"
      _focus={{
         boxShadow: 'outline',
         outline: 'none',
      }}
      {...props}
   />
));
WordmarkLink.displayName = 'WordmarkLink';

export const HeaderOpenHiddenBarButton = forwardRef<HTMLButtonElement, IconButtonProps>(
   ({ onClick, ...otherProps }, ref) => {
      const context = useHeaderContext();
      return (
         <IconButton
            ref={ref}
            variant="ghost"
            minW="30px"
            display={{
               md: 'none',
            }}
            _hover={{
               bg: 'gray.800',
            }}
            _active={{
               bg: 'gray.800',
            }}
            onClick={event => {
               context.hiddenBar.open();
               onClick?.(event);
            }}
            {...otherProps}
         />
      );
   }
);
HeaderOpenHiddenBarButton.displayName = 'HeaderOpenHiddenBarButton';

export const HeaderCloseHiddenBarButton = forwardRef<HTMLButtonElement, ButtonProps>(
   (props, ref) => {
      const context = useHeaderContext();
      return (
         <Button
            ref={ref}
            variant="ghost"
            colorScheme="white"
            _hover={{
               bg: 'gray.800',
            }}
            _active={{
               bg: 'gray.800',
            }}
            mb="-1px"
            fontSize="sm"
            fontWeight="normal"
            onClick={context.hiddenBar.close}
            {...props}
         />
      );
   }
);
HeaderCloseHiddenBarButton.displayName = 'HeaderCloseHiddenBarButton';
